import type { ResolvedMonospacePage } from '#layers/data-model/public';
import { useOverlay } from '@nuxt/ui/composables';
import { defineAsyncComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from '#app/composables/router';
import { useStore } from '#imports';
import { showConfirmationDialog, useToastResultHandling } from '#layers/ui/public';
import { useWorkspaceStore } from '#layers/workspace/public';
import { usePageNavigation } from './use-page-navigation';

const LazySpacePageCreate = defineAsyncComponent(() => import('#layers/spaces/components/Space/PageCreate.vue'));

export function usePageActions() {
	const store = useStore();
	const route = useRoute();
	const router = useRouter();
	const { t } = useI18n();
	const { currentWorkspaceSlug } = useWorkspaceStore();
	const { editPage } = usePageNavigation();
	const overlay = useOverlay();

	function createPage(spaceId: string, sectionId?: string | null) {
		const modal = overlay.create(LazySpacePageCreate, {
			props: {
				spaceId,
				sectionId,
			},
		});
		modal.open();
	}

	const deleteToaster = useToastResultHandling({
		success: {
			title: t('page.delete_page_success'),
		},
		error: {
			title: t('page.delete_page_error'),
		},
	});

	async function deletePage(page: ResolvedMonospacePage) {
		try {
			await store.MonospacePage.delete(page.id);

			deleteToaster.onSuccess();

			if (route.query.redirect) {
				const redirectPath = String(route.query.redirect);
				router.replace(redirectPath);
			}
			else {
				const spaceId = page.spaceId;

				router.replace({
					name: spaceId ?? 'workspace-home',
					params: {
						workspace: currentWorkspaceSlug.value,
					},
				});
			}
		}
		catch (error) {
			deleteToaster.onError(error);
		}
	}

	async function confirmDeletePage(page: ResolvedMonospacePage | null) {
		if (!page) return;

		if (await showConfirmationDialog({
			props: {
				title: t('page.delete_page_confirm.title', {
					name: page.title,
				}),
				description: t('page.delete_page_confirm.description'),
				confirm: {
					label: t('page.delete_page'),
					color: 'error',
				},
			},
		})) {
			deletePage(page);
		}
	}

	return {
		createPage,
		confirmDeletePage,
		editPage,
	};
}
